import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, HeartHandshake, Handshake, Building2, Siren, Ticket, BarChart3, Layers, Inbox, LogOut, Search, ShieldCheck, Bell, Phone, ClipboardList, MessageSquare } from "lucide-react";
import { Logo } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const NAV = [
  { to: "/app/admin", icon: LayoutDashboard, label: "Today", end: true },
  { to: "/app/admin/requests", icon: ClipboardList, label: "Requests" },
  { to: "/app/admin/staff", icon: ShieldCheck, label: "Staff", adminOnly: true },
  { to: "/app/admin/customers", icon: Users, label: "Customers" },
  { to: "/app/admin/care-managers", icon: HeartHandshake, label: "Care Managers" },
  { to: "/app/admin/services", icon: Handshake, label: "Services", mgrOnly: true },
  { to: "/app/admin/providers", icon: Building2, label: "Providers", mgrOnly: true },
  { to: "/app/admin/emergency", icon: Siren, label: "Emergency" },
  { to: "/app/admin/cases", icon: Ticket, label: "Cases" },
  { to: "/app/admin/reports", icon: BarChart3, label: "Reports", mgrOnly: true },
  { to: "/app/admin/plans", icon: Layers, label: "Plans" },
  { to: "/app/admin/leads", icon: Inbox, label: "Leads" },
  { to: "/app/admin/notifications", icon: Bell, label: "Notifications" },
  { to: "/app/admin/settings/whatsapp", icon: MessageSquare, label: "WhatsApp", superOnly: true },
  { to: "/app/admin/profile", icon: Phone, label: "My Alerts" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    const r = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
    setResults(r.data);
  };
  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-portal">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-emerald-950 text-white">
        <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo light />
            <span className="rounded-full bg-terracotta px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">Ops Console</span>
          </div>
          <form onSubmit={search} className="relative hidden w-96 md:block" data-testid="admin-search-form">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parent, family, mobile, Care Manager, case ID…"
              className="w-full rounded-full border border-white/15 bg-white/10 py-2 pl-9 pr-4 text-sm text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="admin-search-input" />
          </form>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-emerald-200 sm:block">{user?.name} · {user?.role?.replace(/_/g, " ")}</span>
            <button onClick={() => { logout(); navigate("/"); }} className="rounded-full p-2 text-emerald-200 hover:bg-white/10" aria-label="Log out" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        {results && (
          <div className="border-t border-white/10 bg-white p-4 text-stone-800 shadow-xl" data-testid="admin-search-results">
            <div className="mx-auto grid max-w-5xl gap-4 text-sm sm:grid-cols-4">
              <div><p className="p1-label">Parents</p>{results.parents.map((p) => <p key={p.id}>{p.name} · {p.zone}</p>)}{!results.parents.length && <p className="text-stone-400">—</p>}</div>
              <div><p className="p1-label">People</p>{results.users.map((u) => <p key={u.id}>{u.name} · {u.role}</p>)}{!results.users.length && <p className="text-stone-400">—</p>}</div>
              <div><p className="p1-label">Care Managers</p>{results.care_managers.map((c) => <p key={c.id}>{c.name}</p>)}{!results.care_managers.length && <p className="text-stone-400">—</p>}</div>
              <div><p className="p1-label">Cases</p>{results.cases.map((c) => <p key={c.id}>{c.title}</p>)}{!results.cases.length && <p className="text-stone-400">—</p>}</div>
            </div>
            <button onClick={() => setResults(null)} className="mt-2 text-xs font-semibold text-emerald-900" data-testid="admin-search-close">Close</button>
          </div>
        )}
      </header>
      <div className="mx-auto flex max-w-[1600px]">
        <aside className="hidden min-h-[calc(100vh-3.5rem)] w-52 shrink-0 border-r border-stone-200 bg-white py-5 lg:block">
          <nav className="space-y-1 px-2" data-testid="admin-side-nav">
            {NAV.filter((n) => (!n.adminOnly || ["admin", "super_admin"].includes(user?.role)) && (!n.superOnly || user?.role === "super_admin") && (!n.mgrOnly || user?.role !== "ops_executive")).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-4 sm:p-6"><Outlet /></main>
      </div>
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex justify-around border-t border-stone-200 bg-white py-2 lg:hidden" data-testid="admin-bottom-nav">
        {NAV.filter((n) => (!n.adminOnly || ["admin", "super_admin"].includes(user?.role)) && (!n.superOnly || user?.role === "super_admin") && (!n.mgrOnly || user?.role !== "ops_executive")).slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-semibold ${isActive ? "text-emerald-900" : "text-stone-400"}`}>
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
